import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import {
  ChatMessageWithParts,
  ContentType,
  UserFileUploadPromptData,
} from "src/views/assistant/types";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { green } from "@mui/material/colors";
import { ACCEPTED_FILE_TYPES } from "src/constants";
import {
  deliverToolOutcome,
  DeliveryResult,
  useDeliveryWatcher,
} from "src/reusable/Chat/ChatStreaming/delivery";
import { NucToast } from "src/reusable/components/Toast";
import { persistToolResponseOutcome } from "src/reusable/Chat/ChatStreaming/services/api";
import { uploadFileUnified } from "src/reusable/services/unifiedChunkedUploadService";
import { useChatStreamStore } from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { resolveOutcome, useStaleUnanswered } from "./resolveOutcome";
import { useChatContext } from "../../context/ChatContext";
import PromptMarkdown from "./PromptMarkdown";
import {
  getInnerResult,
  isToolResponseTextComplete,
  parseToolResponseText,
  patchToolResponseOutcome,
} from "./toolStatusUtils";

interface UserFileUploadPromptProps {
  /** Raw text containing JSON prompt data */
  text: string;
  /** Whether the chat is still streaming */
  isChatConnected: boolean;
  /** Message ID this prompt belongs to (for updating the dialogue) */
  messageId?: string;
  /** Index of this content part within the message */
  partIndex?: number;
}

interface ExistingUpload {
  dataset_id: number;
  file_name: string;
  source_data_record_id?: number;
}

const isValidExistingUpload = (u: unknown): u is ExistingUpload =>
  u !== null &&
  typeof u === "object" &&
  typeof (u as { dataset_id: unknown }).dataset_id === "number" &&
  typeof (u as { file_name: unknown }).file_name === "string";

/**
 * UserFileUploadPrompt Component
 *
 * Renders an inline upload row when an agent requests a document
 * mid-conversation (tool result type "user_file_upload_prompt"), using the
 * AA Projects upload idiom (ProjectBrowse): small outlined Upload button,
 * dashed drop overlay while dragging, caption + LinearProgress while
 * uploading, errors as an Alert. On drop or selection it uploads through
 * the ad-hoc dialogue upload pipeline (the shared dialogue uploads dataset,
 * which is already in the conversation's search scope and mounted at the
 * agent's uploads/ directory) and reports the result back to the agent
 * through the conversation input queue.
 */
const UserFileUploadPrompt = memo(
  ({
    text,
    isChatConnected,
    messageId,
    partIndex,
  }: UserFileUploadPromptProps) => {
    const { dialogueId } = useChatContext();
    // Not useAtomAssistChat: it would clobber the host's stream config (see
    // useChatStreamStore).
    const streamStore = useChatStreamStore();
    const trackDelivery = useDeliveryWatcher();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragActive, setIsDragActive] = useState(false);
    const [progress, setProgress] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [submittedUpload, setSubmittedUpload] =
      useState<ExistingUpload | null>(null);
    const [isDeclined, setIsDeclined] = useState(false);

    // Parse the prompt data and check for an existing outcome (from history)
    const { promptData, existingUpload, existingDecline } = useMemo<{
      promptData: UserFileUploadPromptData | null;
      existingUpload: ExistingUpload | null;
      existingDecline: boolean;
    }>(() => {
      const inner = getInnerResult(parseToolResponseText(text));
      if (!inner || inner.type !== "user_file_upload_prompt") {
        return {
          promptData: null,
          existingUpload: null,
          existingDecline: false,
        };
      }
      return {
        promptData: inner as UserFileUploadPromptData,
        existingUpload: isValidExistingUpload(inner.user_upload)
          ? (inner.user_upload as ExistingUpload)
          : null,
        existingDecline: inner.user_declined === true,
      };
    }, [text]);

    // Collapse the form if the user already answered in plain text (later User message, no outcome).
    const isStaleUnanswered = useStaleUnanswered(messageId);

    // Rewrite this part's text in the host store so the outcome survives a
    // re-render; an `undefined` value strips its key again. True only when
    // the part was actually rewritten: the flag selects continueConversation
    // in the delivery cascade, which must not resume a turn whose dialogue
    // never carried the outcome.
    const patchPart = useCallback(
      (outcome: Record<string, unknown>): boolean => {
        if (
          messageId === undefined ||
          partIndex === undefined ||
          streamStore === null
        ) {
          return false;
        }
        let patched = false;
        streamStore
          .getState()
          .setDialogue((prevDialogue: ChatMessageWithParts[]) =>
            prevDialogue.map((msg) => {
              if (msg.id !== messageId) return msg;
              const updatedParts = msg.contentParts.map((part, idx) => {
                if (idx !== partIndex) return part;
                if (part.type !== ContentType.TOOL_RESPONSE) return part;
                const text = patchToolResponseOutcome(part.text, outcome);
                if (text === null) return part;
                patched = true;
                return { ...part, text };
              });
              return { ...msg, contentParts: updatedParts };
            }),
          );
        return patched;
      },
      [messageId, partIndex, streamStore],
    );

    // Persist the outcome locally (instant flip) and server-side (survives reload).
    const persistOutcome = useCallback(
      (outcome: Record<string, unknown>) => {
        let persisted: Promise<boolean> = Promise.resolve(false);
        if (promptData?.prompt_id && dialogueId !== undefined) {
          persisted = persistToolResponseOutcome(
            dialogueId,
            promptData.prompt_id,
            outcome,
          );
          void persisted.then((ok) => {
            if (!ok) {
              console.error("[UserFileUploadPrompt:outcome-persist-failed]", {
                dialogueId,
              });
            }
          });
        }
        return { patched: patchPart(outcome), persisted };
      },
      [patchPart, dialogueId, promptData?.prompt_id],
    );

    // Live delivery leg. Null when no host store resolves: only the durable
    // persist carries the outcome then. Old backends drop the unknown
    // tool_outcome field and deliver `content` as a plain user message; new
    // ones ignore `content`.
    const sendOutcome = useCallback(
      async (
        outcome: Record<string, unknown>,
        fallback: string,
        patched: boolean,
      ): Promise<DeliveryResult | null> => {
        if (streamStore === null || !promptData?.prompt_id) {
          console.warn(
            "[UserFileUploadPrompt:no-host-store] live delivery unavailable",
            { dialogueId },
          );
          return null;
        }
        const result = await deliverToolOutcome(streamStore, {
          dialogueId,
          promptId: promptData.prompt_id,
          toolOutcome: outcome,
          fallbackMessage: fallback,
          patched,
        });
        if (result.dispatch === "rejected") {
          console.error("[UserFileUploadPrompt:outcome-send-failed]", {
            dialogueId,
            promptId: promptData.prompt_id,
          });
        }
        return result;
      },
      [dialogueId, streamStore, promptData?.prompt_id],
    );

    // Supported chat formats, narrowed to the tool's requested extensions when
    // they overlap. Dot-normalized: the tool may send "pdf" or ".pdf".
    const requested = useMemo(
      () =>
        (promptData?.accepted_file_types ?? []).map((e) => {
          const ext = e.trim().toLowerCase();
          return ext.startsWith(".") ? ext : `.${ext}`;
        }),
      [promptData?.accepted_file_types],
    );
    const acceptExtensions = useMemo(() => {
      const supported = ACCEPTED_FILE_TYPES.EXTENSIONS.CHAT_PROJECT;
      const intersection = supported.filter((ext) => requested.includes(ext));
      return intersection.length ? intersection : supported;
    }, [requested]);
    const isNarrowed =
      acceptExtensions.length <
      ACCEPTED_FILE_TYPES.EXTENSIONS.CHAT_PROJECT.length;

    const uploadChosen = useCallback(
      async (chosen: File) => {
        // The native picker enforces accept=; drops bypass it, so re-check here.
        const ext = chosen.name.includes(".")
          ? "." + (chosen.name.split(".").pop() ?? "").toLowerCase()
          : "";
        if (!acceptExtensions.includes(ext)) {
          setErrorMessage(
            `Unsupported file type${ext ? ` ${ext}` : ""}. ` +
              `Accepted: ${acceptExtensions.join(", ")}`,
          );
          return;
        }
        setFile(chosen);
        setIsUploading(true);
        setErrorMessage(null);
        setProgress(0);
        try {
          // Lands in the dialogue uploads dataset (in search scope) and the agent's uploads/ mount.
          const response: any = await uploadFileUnified({
            context: "dialogue",
            contextId: String(dialogueId),
            file: chosen,
            onProgress: (p: number) => setProgress(p),
          });
          const result = response?.result ?? response;

          const fileError = result?.errors?.find(
            (e: { filename?: string }) => e.filename === chosen.name,
          );
          if (fileError) throw new Error(fileError.error);
          if (!result?.dataset_id || !result?.uploaded_files?.length) {
            throw new Error(
              "Upload succeeded but the server response is incomplete. " +
                "Please try again.",
            );
          }
          // A single entry is our upload even if the server renamed it.
          const uploadedFile =
            result.uploaded_files.length === 1
              ? result.uploaded_files[0]
              : result.uploaded_files.find(
                  (f: { filename?: string }) => f.filename === chosen.name,
                );
          if (!uploadedFile) {
            throw new Error(
              "Upload succeeded but the server response does not include " +
                "the uploaded file. Please try again.",
            );
          }

          // No dataset_name: internal names must not reach the model or UI.
          const outcome: ExistingUpload = {
            dataset_id: result.dataset_id,
            file_name: chosen.name,
            source_data_record_id: uploadedFile?.source_data_record_id,
          };
          setSubmittedUpload(outcome);
          // Tray owns file status/management; the inline row is just the record.
          useChatStore.getState().addUploadedFile({
            file: chosen,
            id: `upload-prompt-${outcome.source_data_record_id ?? chosen.name}`,
            datasetId: outcome.dataset_id,
            sourceDataRecordId: outcome.source_data_record_id,
            origin: "dialogue",
            uploadStatus: "uploaded",
            uploadProgress: 100,
            processingStatus: "pending",
            processingProgress: 0,
          });
          const uploadOutcome = {
            user_upload: {
              ...outcome,
              submitted_at: new Date().toISOString(),
            },
          };
          const { patched } = persistOutcome(uploadOutcome);
          const delivery = await sendOutcome(
            uploadOutcome,
            `I uploaded "${chosen.name}".`,
            patched,
          );
          // The upload and its durable outcome landed; when the live turn
          // does not follow, the model learns it on the next turn.
          if (delivery?.dispatch === "sent") {
            void trackDelivery(delivery).then((verdict) => {
              if (verdict === "undelivered") {
                console.error("[UserFileUploadPrompt:undelivered]", {
                  dialogueId,
                  promptId: promptData?.prompt_id,
                });
              }
            });
          }
        } catch (error) {
          setErrorMessage(
            error instanceof Error ? error.message : "Upload failed",
          );
        } finally {
          setIsUploading(false);
        }
      },
      [
        acceptExtensions,
        dialogueId,
        persistOutcome,
        sendOutcome,
        trackDelivery,
        promptData?.prompt_id,
      ],
    );

    const handleFileChosen = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const chosen = event.target.files?.[0];
        // Reset so picking the same file after an error re-fires onChange.
        event.target.value = "";
        if (chosen) void uploadChosen(chosen);
      },
      [uploadChosen],
    );

    const handleDragOver = useCallback(
      (event: React.DragEvent) => {
        event.preventDefault();
        if (!isUploading) setIsDragActive(true);
      },
      [isUploading],
    );

    const handleDrop = useCallback(
      (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragActive(false);
        if (isUploading) return;
        const dropped = event.dataTransfer.files[0];
        if (dropped) void uploadChosen(dropped);
      },
      [isUploading, uploadChosen],
    );

    // A decline has no upload to fall back on: when neither the live leg nor
    // the durable persist reached the backend, the turn stays parked, so the
    // row returns to answerable instead of showing a skipped state.
    const handleDecline = useCallback(async () => {
      setIsDeclined(true);
      const outcome = { user_declined: true };
      const { patched, persisted } = persistOutcome(outcome);
      const revertUnlessPersisted = async () => {
        if (await persisted) return;
        setIsDeclined(false);
        patchPart({ user_declined: undefined });
        NucToast({
          message: "Your answer could not be sent. Please try again.",
          type: "error",
        });
      };
      const result = await sendOutcome(
        outcome,
        "I can't provide the requested file.",
        patched,
      );
      if (result === null || result.dispatch === "rejected") {
        await revertUnlessPersisted();
        return;
      }
      void trackDelivery(result).then((verdict) => {
        if (verdict !== "undelivered") return;
        console.error("[UserFileUploadPrompt:undelivered]", {
          dialogueId,
          promptId: promptData?.prompt_id,
        });
        void revertUnlessPersisted();
      });
    }, [
      persistOutcome,
      patchPart,
      sendOutcome,
      trackDelivery,
      dialogueId,
      promptData?.prompt_id,
    ]);

    const isContentComplete = useMemo(
      () => isToolResponseTextComplete(text, isChatConnected),
      [text, isChatConnected],
    );

    const completedUpload = resolveOutcome({
      local: submittedUpload,
      partText: existingUpload,
    });
    const declined = isDeclined || existingDecline;

    if (!promptData || !isContentComplete) {
      return null;
    }

    // Outcome record only — file status/management lives in the attachment tray.
    if (completedUpload || declined || isStaleUnanswered) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: "action.hover",
            my: 1,
          }}
        >
          {completedUpload || declined ? (
            <CheckCircleIcon
              fontSize="small"
              sx={{ color: completedUpload ? green[500] : "text.disabled" }}
            />
          ) : (
            <RadioButtonUncheckedIcon
              fontSize="small"
              sx={{ color: "text.disabled" }}
            />
          )}
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {completedUpload
                ? completedUpload.file_name
                : declined
                  ? "File upload skipped"
                  : "File upload request"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {completedUpload
                ? "Uploaded — see attachments above the message box"
                : declined
                  ? ""
                  : "Not answered"}
            </Typography>
          </Box>
        </Box>
      );
    }

    // Open form — AA Projects (ProjectBrowse) upload idiom.
    return (
      <Box
        sx={{ position: "relative", my: 1 }}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={handleDrop}
        data-attr="chat:form:upload-prompt-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={acceptExtensions.join(",")}
          onChange={handleFileChosen}
        />
        {isDragActive && !isUploading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              border: "2px dashed",
              borderColor: "primary.main",
              borderRadius: 1,
              backgroundColor: "action.hover",
              pointerEvents: "none",
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 40, color: "primary.main" }} />
            <Typography variant="h6" color="primary.main">
              Drop file to upload
            </Typography>
          </Box>
        )}
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <PromptMarkdown text={promptData.prompt} sx={{ mb: 1.5 }} />
          {isUploading ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Uploading {file?.name}… {Math.round(progress)}%
              </Typography>
              <LinearProgress
                variant={progress > 0 ? "determinate" : "indeterminate"}
                value={progress}
              />
            </Box>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudUploadIcon fontSize="small" />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ whiteSpace: "nowrap" }}
                data-attr="chat:btn:upload-prompt-primary"
              >
                Upload file
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => void handleDecline()}
                sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                data-attr="chat:btn:upload-prompt-decline"
              >
                Skip
              </Button>
              {isNarrowed && (
                <Typography variant="caption" color="text.secondary">
                  {acceptExtensions.join(", ")}
                </Typography>
              )}
            </Stack>
          )}
        </Paper>
        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mt: 1 }}
            onClose={() => setErrorMessage(null)}
          >
            {errorMessage}
          </Alert>
        )}
      </Box>
    );
  },
);

UserFileUploadPrompt.displayName = "UserFileUploadPrompt";

export default UserFileUploadPrompt;
